import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Layout } from "@/components/layout";
import { ProtectedRoute } from "@/components/auth/protected-route";
import { ScrollToTop } from "@/components/utils/ScrollToTop";
import { LoadingState } from "@/components/common/loading-state";

// --- Lazy Loaded Pages ---
const Login = lazy(() => import("@/pages/common/login"));
const ForgotPassword = lazy(() => import("@/pages/common/forgot-password"));
const ResetPassword = lazy(() => import("@/pages/common/reset-password"));
const Verify = lazy(() => import("@/pages/common/verify"));
const AuthCallback = lazy(() => import("@/pages/common/auth-callback"));
const Forbidden = lazy(() => import("@/pages/common/forbidden"));
const NotFound = lazy(() => import("@/pages/common/not-found"));
const PublicComplaintForm = lazy(() => import("@/pages/public/reclamaciones/form"));

// Admin
const Sucursales = lazy(() => import("@/pages/admin/branches/page"));
const UsersList = lazy(() => import("@/pages/admin/users/page"));
const UserForm = lazy(() => import("@/pages/admin/users/form"));
const ComplaintsAdmin = lazy(() => import("@/pages/admin/reclamaciones/page"));
const ComplaintDetail = lazy(() => import("@/pages/admin/reclamaciones/detail"));
const AttendancePage = lazy(() => import("@/pages/admin/users/attendance"));

// Tenant & Core
const TenantLayout = lazy(() => import("@/pages/common/tenant-layout"));
const Dashboard = lazy(() => import("@/pages/common/dashboard"));

// Sections
const SettingsLayout = lazy(() => import("@/pages/admin/settings/layout"));
const Settings = lazy(() => import("@/pages/admin/settings/settings"));
const Account = lazy(() => import("@/pages/admin/settings/account"));
const Notifications = lazy(() => import("@/pages/admin/settings/notifications"));
const SunatSettingsPage = lazy(() => import("@/pages/admin/settings/sunat"));
const DigemidSettingsPage = lazy(() => import("@/pages/admin/settings/digemid"));
const IndecopiSettingsPage = lazy(() => import("@/pages/admin/settings/indecopi"));
const SunatAuditPage = lazy(() => import("@/pages/admin/sunat/audit"));

// Catalogue
const CatalogueLayout = lazy(() => import("@/pages/admin/catalogue/layout"));
const CatalogueList = lazy(() => import("@/pages/admin/catalogue/list"));
const ProductForm = lazy(() => import("@/pages/admin/catalogue/product-form"));
const ImportCataloguePage = lazy(() => import("@/pages/admin/catalogue/ImportPage"));
const Categories = lazy(() => import("@/pages/admin/catalogue/categories"));
const Laboratories = lazy(() => import("@/pages/admin/catalogue/laboratories"));
const Units = lazy(() => import("@/pages/admin/catalogue/units"));
const DiscountListPage = lazy(() => import("@/pages/admin/catalogue/discounts"));
const DiscountFormPage = lazy(() => import("@/pages/admin/catalogue/discount-form"));

// Sales
const SalesLayout = lazy(() => import("@/pages/admin/sales/layout"));
const SalesHistory = lazy(() => import("@/pages/admin/sales/history"));
const DigemidPortalPage = lazy(() => import("@/pages/admin/digemid/portal"));
const SaleDetail = lazy(() => import("@/pages/admin/sales/detail"));

// Customers
const CustomersLayout = lazy(() => import("@/pages/admin/customers/layout"));
const Customers = lazy(() => import("@/pages/admin/customers/list"));
const CustomerForm = lazy(() => import("@/pages/admin/customers/form"));
const CustomerDetail = lazy(() => import("@/pages/admin/customers/detail"));

// People (Unified)
const CustomerRegistration = lazy(() => import("@/pages/admin/customers/registration"));

// Inventory
const InventoryLayout = lazy(() => import("@/pages/admin/inventory/layout"));
const InventoryList = lazy(() => import("@/pages/admin/inventory/list"));
const InventoryMovements = lazy(() => import("@/pages/admin/inventory/movements"));
const Suppliers = lazy(() => import("@/pages/admin/inventory/suppliers"));
const InventoryEdit = lazy(() => import("@/pages/admin/inventory/edit"));
const InventoryIntake = lazy(() => import("@/pages/admin/inventory/intake"));

// Treasury
const TreasuryLayout = lazy(() => import("@/pages/admin/treasury/layout"));
const CashClosurePage = lazy(() => import("@/pages/admin/treasury/cash-closure"));
const ExpensesPage = lazy(() => import("@/pages/admin/treasury/expenses"));
const ExpenseForm = lazy(() => import("@/pages/admin/treasury/expense-form"));
const CyclicInventoryPage = lazy(() => import("@/pages/admin/inventory/cyclic"));
const ReportsPage = lazy(() => import("@/pages/admin/reports/general"));
const POSPage = lazy(() => import("@/pages/admin/sales/pos"));

// Finance
const FinanceLayout = lazy(() => import("@/pages/admin/finance/layout"));
const AccountsPayablePage = lazy(() => import("@/pages/admin/finance/accounts-payable"));
const AccountPayableForm = lazy(() => import("@/pages/admin/finance/account-payable-form"));
const BalancesPage = lazy(() => import("@/pages/admin/finance/balances"));
const CreateBranch = lazy(() => import("@/pages/admin/branches/CreateBranch"));
const EditBranch = lazy(() => import("@/pages/admin/branches/EditBranch"));

/**
 * Main entry point for the Pharmacy POS system.
 */
function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Suspense fallback={<LoadingState message="Cargando seccion..." />}>
        <Routes>
          <Route element={<Layout />}>
            <Route index element={<Login />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="reset-password" element={<ResetPassword />} />
            <Route path="verify" element={<Verify />} />
            <Route path="auth/callback" element={<AuthCallback />} />
            <Route path="libro-de-reclamaciones" element={<PublicComplaintForm />} />

            <Route
              path=":tenant"
              element={
                <ProtectedRoute>
                  <TenantLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="panel" element={<Dashboard />} />
              <Route
                path="portal-opps"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <DigemidPortalPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="sucursales"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <Outlet />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Sucursales />} />
                <Route path="nueva" element={<CreateBranch />} />
                <Route path="editar/:id" element={<EditBranch />} />
              </Route>
              <Route
                path="usuarios"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <Outlet />
                  </ProtectedRoute>
                }
              >
                <Route index element={<UsersList />} />
                <Route path="nuevo" element={<UserForm />} />
                <Route path=":id/editar" element={<UserForm />} />
              </Route>

              <Route
                path="asistencia"
                element={
                  <ProtectedRoute>
                    <AttendancePage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="configuracion"
                element={
                  <ProtectedRoute allowedRoles={["admin", "cajero", "inventario", "ventas"]}>
                    <SettingsLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Settings />} />
                <Route path="cuenta" element={<Account />} />
                <Route path="notificaciones" element={<Notifications />} />
                <Route path="sunat" element={<SunatSettingsPage />} />
                <Route path="digemid" element={<DigemidSettingsPage />} />
                <Route path="indecopi" element={<IndecopiSettingsPage />} />
              </Route>

              <Route
                path="sunat-auditoria"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <SunatAuditPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="catalogo-productos"
                element={
                  <ProtectedRoute allowedRoles={["admin", "inventario"]}>
                    <CatalogueLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<CatalogueList />} />
                <Route path="importar" element={<ImportCataloguePage />} />
                <Route path="nuevo" element={<ProductForm />} />
                <Route path=":id/editar" element={<ProductForm />} />
              </Route>

              <Route
                path="catalogo-categorias"
                element={
                  <ProtectedRoute allowedRoles={["admin", "inventario"]}>
                    <Categories />
                  </ProtectedRoute>
                }
              />
              <Route
                path="catalogo-laboratorios"
                element={
                  <ProtectedRoute allowedRoles={["admin", "inventario"]}>
                    <Laboratories />
                  </ProtectedRoute>
                }
              />
              <Route
                path="catalogo-unidades"
                element={
                  <ProtectedRoute allowedRoles={["admin", "inventario"]}>
                    <Units />
                  </ProtectedRoute>
                }
              />
              <Route
                path="catalogo-descuentos"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <CatalogueLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<DiscountListPage />} />
                <Route path="nuevo" element={<DiscountFormPage />} />
                <Route path=":id/editar" element={<DiscountFormPage />} />
              </Route>

              <Route
                path="ventas"
                element={
                  <ProtectedRoute allowedRoles={["admin", "ventas", "cajero"]}>
                    <SalesLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="historial" replace />} />
                <Route path="historial" element={<SalesHistory />} />
                <Route path=":id" element={<SaleDetail />} />
                <Route path="pos" element={<POSPage />} />
              </Route>

              <Route
                path="clientes"
                element={
                  <ProtectedRoute allowedRoles={["admin", "ventas", "cajero"]}>
                    <CustomersLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Customers />} />
                <Route path="personas" element={<Customers mode="person" />} />
                <Route path="empresas" element={<Customers mode="company" />} />
                <Route path="registro" element={<CustomerRegistration />} />
                <Route path="nuevo" element={<Navigate to="../registro" replace={true} />} />
                <Route path=":id" element={<CustomerDetail />} />
                <Route path=":id/editar" element={<CustomerForm />} />
              </Route>

              <Route
                path="inventario"
                element={
                  <ProtectedRoute allowedRoles={["admin", "ventas", "inventario"]}>
                    <InventoryLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<InventoryList />} />
                <Route path="ingresar" element={<InventoryIntake />} />
                <Route
                  path="ciclico"
                  element={
                    <ProtectedRoute allowedRoles={["admin", "inventario"]}>
                      <CyclicInventoryPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="movimientos" element={<InventoryMovements />} />
                <Route path="proveedores" element={<Suppliers />} />
                <Route path="editar/:id" element={<InventoryEdit />} />
              </Route>

              <Route
                path="tesoreria"
                element={
                  <ProtectedRoute allowedRoles={["admin", "cajero", "ventas"]}>
                    <TreasuryLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="cierre/:id?" element={<CashClosurePage />} />
                <Route path="egresos" element={<ExpensesPage />} />
                <Route path="egresos/nuevo" element={<ExpenseForm />} />
                <Route path="egresos/:id/editar" element={<ExpenseForm />} />
              </Route>

              <Route
                path="finanzas"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <FinanceLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="cuentas-pagar" element={<AccountsPayablePage />} />
                <Route path="cuentas-pagar/nuevo" element={<AccountPayableForm />} />
                <Route path="cuentas-pagar/:id/editar" element={<AccountPayableForm />} />
                <Route path="balances" element={<BalancesPage />} />
              </Route>

              <Route
                path="reportes"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <ReportsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="reclamaciones"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <Outlet />
                  </ProtectedRoute>
                }
              >
                <Route index element={<ComplaintsAdmin />} />
                <Route path=":id" element={<ComplaintDetail />} />
              </Route>
            </Route>

            <Route path="forbidden" element={<Forbidden />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
